import { useState } from 'react';
import { X, Loader2, PlusCircle } from 'lucide-react';
import { toast } from 'sonner';

// Formulaire de création d'un nouvel exercice (admin) — une fois créé, l'exercice s'ouvre
// directement dans le détail où l'admin peut générer image/texte/vidéo via ExerciseAdminTools.
export const NewExerciseModal = ({ en, meta, authFetch, onClose, onCreated }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const [form, setForm] = useState({
    name_fr: '', name_en: '', muscle_group: meta.muscle_groups[0] || '',
    equipment: (meta.equipments[0] && meta.equipments[0].key) || '',
    level: (meta.levels[0] && meta.levels[0].key) || '',
    type: 'musculation',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const submit = async () => {
    if (!form.name_fr.trim() || !form.muscle_group || !form.equipment || !form.level) {
      toast.error(L('Nom, groupe musculaire, matériel et niveau sont requis', 'Name, muscle group, equipment and level are required'));
      return;
    }
    setSaving(true);
    try {
      const res = await authFetch('/api/exercises', {
        method: 'POST',
        body: JSON.stringify({ ...form, name_en: form.name_en.trim() || form.name_fr.trim() }),
      });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      toast.success(L('Exercice créé — ajoutez maintenant image/texte/vidéo', 'Exercise created — now add image/text/video'));
      onCreated(data);
    } catch { toast.error(L('Échec de la création', 'Creation failed')); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/85 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose} data-testid="new-exercise-modal">
      <div className="bg-gradient-to-br from-zinc-900 to-black border border-primary/30 rounded-3xl max-w-md w-full max-h-[92vh] overflow-y-auto p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-black text-white flex items-center gap-2"><PlusCircle className="w-5 h-5 text-primary" /> {L('Nouvel exercice', 'New exercise')}</h3>
          <button onClick={onClose} data-testid="new-exercise-close-btn"><X className="w-5 h-5 text-zinc-400 hover:text-white" /></button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs text-zinc-400 font-bold">{L('Nom (FR) *', 'Name (FR) *')}</label>
            <input value={form.name_fr} onChange={(e) => set('name_fr', e.target.value)}
              className="w-full mt-1 bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
              data-testid="new-exercise-name-fr" />
          </div>
          <div>
            <label className="text-xs text-zinc-400 font-bold">{L('Nom (EN, optionnel)', 'Name (EN, optional)')}</label>
            <input value={form.name_en} onChange={(e) => set('name_en', e.target.value)}
              className="w-full mt-1 bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
              data-testid="new-exercise-name-en" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-400 font-bold">{L('Groupe musculaire *', 'Muscle group *')}</label>
              <select value={form.muscle_group} onChange={(e) => set('muscle_group', e.target.value)}
                className="w-full mt-1 bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                data-testid="new-exercise-muscle-group">
                {meta.muscle_groups.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-zinc-400 font-bold">{L('Matériel *', 'Equipment *')}</label>
              <select value={form.equipment} onChange={(e) => set('equipment', e.target.value)}
                className="w-full mt-1 bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                data-testid="new-exercise-equipment">
                {meta.equipments.map((eq) => <option key={eq.key} value={eq.key}>{eq.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-zinc-400 font-bold">{L('Niveau *', 'Level *')}</label>
              <select value={form.level} onChange={(e) => set('level', e.target.value)}
                className="w-full mt-1 bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                data-testid="new-exercise-level">
                {meta.levels.map((lv) => <option key={lv.key} value={lv.key}>{lv.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-zinc-400 font-bold">Type</label>
              <select value={form.type} onChange={(e) => set('type', e.target.value)}
                className="w-full mt-1 bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                data-testid="new-exercise-type">
                <option value="musculation">{L('Musculation', 'Strength')}</option>
                <option value="cardio">Cardio</option>
              </select>
            </div>
          </div>
        </div>

        <button onClick={submit} disabled={saving}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-primary to-pink-500 disabled:opacity-50"
          data-testid="new-exercise-submit-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlusCircle className="w-4 h-4" />}
          {L('Créer l\'exercice', 'Create exercise')}
        </button>
        <p className="text-[11px] text-zinc-500 italic text-center">{L(
          "Vous pourrez ensuite générer l'image, le texte d'exécution et une vidéo depuis le détail de l'exercice.",
          "You'll then be able to generate the image, execution text and a video from the exercise detail."
        )}</p>
      </div>
    </div>
  );
};
